/**
 * Barra lateral del curso.
 *
 * Solo aparece en las paginas sueltas: progreso, repaso y ajustes.
 * En la leccion manda el temario del reproductor, asi que aqui no se
 * repiten los modulos.
 */

import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Home, Sparkles, Settings, BarChart3, NotebookPen } from 'lucide-react'
import { useCourseOptional } from '@/contexts/CourseContext'
import { useReviewDeck } from '@/hooks/useReviewDeck'
import { contentModules } from '@/lib/coursesRegistry'
import { useQuery } from '@tanstack/react-query'
import { fetchCourseCompletion } from '@/lib/blockDb'
import { useAuth } from '@/contexts/AuthContext'

interface NavSidebarProps {
  open: boolean
  onClose: () => void
}

const linkClass = ({ isActive }: { isActive: boolean }) =>
  [
    'mw-sidebar-link',
    isActive ? 'active' : '',
  ].join(' ')

export function NavSidebar({ open, onClose }: NavSidebarProps) {
  const { t } = useTranslation('mw')
  const courseCtx = useCourseOptional()
  const course = courseCtx?.course ?? null

  const href = (path = '') =>
    courseCtx ? courseCtx.href(path) : `/${path}`

  const { user } = useAuth()

  const { data: completion = new Map() } = useQuery({
    queryKey: ['course-completion', course?.slug, user?.id],
    queryFn: () => fetchCourseCompletion(course!.slug),
    enabled: Boolean(course?.slug && user?.id),
    staleTime: 30_000,
  })

  const mods = course ? contentModules(course) : []
  const doneMods = mods.filter(m => completion.get(m.slug)?.completed).length
  const pct = mods.length > 0 ? Math.round((doneMods / mods.length) * 100) : 0

  return (
    <>
      {/* Fondo en movil */}
      {open && (
        <div
          className="fixed inset-0 z-30 lg:hidden"
          style={{ background: 'rgba(11,14,26,0.45)' }}
          onClick={onClose}
          aria-hidden
        />
      )}

      <nav
        className={[
          'fixed lg:sticky top-0 lg:top-[var(--layout-header-h)] left-0 z-40 lg:z-0',
          /* Ocupa lo que queda bajo la barra, que es adherente y
             mide 56px. Con h-dvh entero se salia por abajo. */
          'h-dvh lg:h-auto lg:min-h-[calc(100dvh-var(--layout-header-h))]',
          'lg:max-h-[calc(100dvh-var(--layout-header-h))]',
          'w-[var(--layout-sidebar-w)] shrink-0',
          'bg-[var(--bg-canvas)] border-r border-[var(--border-default)]',
          'overflow-y-auto overscroll-contain',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        ].join(' ')}
        aria-label="Course navigation"
      >
        <div className="py-4 px-3 space-y-0.5">
          <NavLink to={href('')} end onClick={onClose} className={linkClass}>
            <Home className="size-[15px] shrink-0 stroke-[1.75]" aria-hidden />
            <span>{t('course.home')}</span>
          </NavLink>

          {/* Avance del curso */}
          {pct > 0 && (
            <div className="px-2.5 py-3">
              <div className="flex items-center justify-between mb-1.5">
                <span style={{ fontSize: 11, color: 'var(--mw-ink-muted)' }}>Tu avance</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--mw-accent)' }}>{pct}%</span>
              </div>
              <div className="mw-progress-bar">
                <div className="mw-progress-bar__fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          <NavLink to={href('progreso')} onClick={onClose} className={linkClass}>
            <BarChart3 className="size-[15px] shrink-0 stroke-[1.75]" aria-hidden />
            <span>{t('course.progress', { defaultValue: 'Progreso' })}</span>
          </NavLink>

          <NavLink to={href('apuntes')} onClick={onClose} className={linkClass}>
            <NotebookPen className="size-[15px] shrink-0 stroke-[1.75]" aria-hidden />
            <span>Apuntes</span>
          </NavLink>

          <RepasoLink onItemClick={onClose} href={href} courseSlug={course?.slug ?? ''} />

          <div className="pt-3 mt-3 border-t border-[var(--border-subtle)]">
            <NavLink to={href('ajustes')} onClick={onClose} className={linkClass}>
              <Settings className="size-[15px] shrink-0 stroke-[1.75]" aria-hidden />
              <span>{t('course.settings')}</span>
            </NavLink>
          </div>
        </div>
      </nav>
    </>
  )
}

/** Repaso, con el numero de tarjetas pendientes si las hay. */
function RepasoLink({ onItemClick, href, courseSlug }: {
  onItemClick?: () => void
  href: (path?: string) => string
  courseSlug: string
}) {
  const { t } = useTranslation('mw')
  const { dueCount } = useReviewDeck(courseSlug)

  return (
    <NavLink to={href('repaso')} onClick={onItemClick} className={linkClass}>
      <Sparkles className="size-[15px] shrink-0 stroke-[1.75]" aria-hidden />
      <span>{t('course.review')}</span>
      {dueCount > 0 && (
        <span
          className="ml-auto inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full tabular-nums"
          style={{
            background: 'var(--bg-active, #EFF4FF)',
            color: 'var(--mw-accent)',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {dueCount}
        </span>
      )}
    </NavLink>
  )
}
